import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  of,
  shareReplay,
  switchMap,
  throwError,
} from "rxjs";
import { odataList } from "./odataApi";
import { ApiSchema } from "@api-types";
import { getClientService } from "./client.service";
import { initService } from "helpers/initService";

type LocalFlags = "beta-enabled";
export type FeatureFlag = Pick<ApiSchema.FeatureFlag, "id" | "ff">;
class FFService {
  private readonly _ffs$ = new BehaviorSubject<FeatureFlag[] | null>(null);
  public readonly ffs$ = this._ffs$.asObservable();
  private readonly localFlags$ = new BehaviorSubject<LocalFlags[]>(
    localStorage?.getItem && localStorage.getItem("beta-enabled") ? ["beta-enabled"] : [],
  );
  constructor() {
    combineLatest([
      odataList("getFeatureFlags").select("id", "ff").filter("isEnabled", "eq", true).$(),
      getClientService().defaultClientId$.pipe(
        filter((r) => !!r),
        switchMap((clientId) =>
          odataList(
            "getClientFeatures",
            {},
            {
              clientId: clientId,
              errorHandler: (err) => {
                if (err.status === 403) {
                  return of([]);
                }
                return throwError(() => err);
              },
            },
          )
            .filter("isEnabled", "eq", true)
            .expandAndSelect("featureFlag", null, "id", "ff")
            .$(),
        ),
      ),
    ])
      .pipe(
        map(([ffs, cfs]) => [
          ...(Array.isArray(ffs.value) && ffs.value.length ? ffs.value : []),
          ...(Array.isArray(cfs.value) && cfs.value.length ? cfs.value.map((cf) => cf.featureFlag) : []),
        ]),
      )
      .subscribe((ffs) => this._ffs$.next(ffs));
  }

  public featureEnabled = (ff: ApiSchema.FF) => !!this._ffs$.value?.find((f) => f.ff === ff);
  public featureEnabled$ = (ff: ApiSchema.FF) =>
    this.ffs$.pipe(
      filter((f) => !!f),
      map((f) => !!f?.find((f) => f.ff === ff)),
      distinctUntilChanged(),
      shareReplay(),
    );

  public toggleLocalFlag = (f: LocalFlags) => {
    if (this.localFlags$.value.includes(f)) {
      localStorage.removeItem(f);
      this.localFlags$.next([]);
    } else {
      localStorage.setItem(f, "true");
      this.localFlags$.next([f]);
    }
  };
  public localFlagEnabled$ = (f: LocalFlags) =>
    this.localFlags$.pipe(
      filter((f) => !!f),
      map((z) => !!z?.find((x) => x === f)),
      distinctUntilChanged(),
      shareReplay(),
    );
}

let ffService: Readonly<FFService> | null = null;

export const getFFService = () => {
  if (!ffService) {
    ffService = initService(() => new FFService());
  }
  return ffService;
};
